import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, CardBody, Container, Input, Spinner, Table } from "reactstrap";

import { fetchAuditLogs } from "../../api/requests/audit-requests";
import { fetchCompaniesList } from "../../api/requests/company-requests";
import { fetchAuthMe } from "../../api/requests/auth-requests";
import AuditDetailDrawer from "../../components/Audit/AuditDetailDrawer";
import { entityTypeLabel } from "../../components/Audit/auditLabels";
import IdValue from "../../components/Audit/IdValue";
import { formatDateTime } from "./Paymenthistoryutils";
import "../../assets/css/page-common.css";

/**
 * Denetim İzi — kullanıcı aktivitelerinin kaydı.
 *
 * Üç davranışı backend belirler, ekran yalnız uygular:
 *
 * 1. **Firma kapsamı oturumdan gelir.** `companyId` filtresi yalnız SUPER_ADMIN'e
 *    gösterilir; firma yöneticisi başka firma isterse backend 403 döner.
 * 2. **Güvenlik olayları firmaya kapalıdır.** Kategori listesinde bu seçenek yalnız
 *    SUPER_ADMIN'e çıkar — gizlemek kolaylık, asıl sınır backend'de.
 * 3. **Platform personeli maskelenir.** Firma yöneticisi süper adminin işlemini
 *    "Sistem Yöneticisi" olarak görür; ne yapıldığı görünür, kim yaptığı görünmez.
 */

const CATEGORIES = [
    { value: "FINANCE", label: "Finans" },
    { value: "USER", label: "Kullanıcı" },
    { value: "COMPANY", label: "Firma" },
    { value: "INTEGRATION", label: "Entegrasyon" },
    { value: "SUBSCRIPTION", label: "Abonelik" },
];

/** Yalnız SUPER_ADMIN görebildiği için ayrı tutuluyor. */
const SECURITY_CATEGORY = { value: "SECURITY", label: "Güvenlik" };

const OUTCOMES = [
    { value: "SUCCESS", label: "Başarılı" },
    { value: "FAILURE", label: "Başarısız" },
    { value: "PARTIAL", label: "Belirsiz" },
];

const OUTCOME_COLORS = { SUCCESS: "success", FAILURE: "danger", PARTIAL: "warning" };

const CATEGORY_COLORS = {
    FINANCE: "#221C5D",
    SECURITY: "#dc3545",
    USER: "#6f42c1",
    COMPANY: "#0d9488",
    INTEGRATION: "#E56A22",
    SUBSCRIPTION: "#2563eb",
};

const PAGE_SIZES = [25, 50, 100];

/** Varsayılan pencere backend ile aynı: filtresiz istek son 30 günü getirir. */
const isoDaysAgo = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 16);
};

const EMPTY_FILTERS = {
    startDate: isoDaysAgo(30),
    endDate: "",
    companyId: "",
    category: "",
    outcome: "",
    referenceKey: "",
    correlationId: "",
};

const styles = `
  .audit-log .cat-dot { display:inline-block; width:8px; height:8px; border-radius:50%; margin-right:.4rem; }
  .audit-log .filter-grid { display:grid; gap:.75rem; grid-template-columns:repeat(4,1fr); }
  @media (max-width:1200px){ .audit-log .filter-grid { grid-template-columns:repeat(2,1fr); } }
  @media (max-width:576px) { .audit-log .filter-grid { grid-template-columns:1fr; } }
  .audit-log .mono { font-family:ui-monospace,SFMono-Regular,Menlo,monospace; font-size:.78rem; }
  .audit-log td { vertical-align:middle; }
  .audit-log .id-value { cursor:pointer; }
  .audit-log .id-value code { color:#221C5D; }
  .audit-log .id-value.is-expanded code { word-break:break-all; }
  .audit-log .header-icon {
      width:46px; height:46px; flex:0 0 46px; border-radius:.85rem;
      display:flex; align-items:center; justify-content:center; font-size:1.35rem;
      background:#EEF3FB; color:#221C5D;
  }
`;

// embedded: Ayarlar sayfasının sekmesi olarak çizilir; sayfa sarmalı ve başlık Ayarlar'dan gelir.
const AuditLogTable = ({ embedded = false }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [draft, setDraft] = useState(EMPTY_FILTERS);
    const [applied, setApplied] = useState(EMPTY_FILTERS);
    const [pageSize, setPageSize] = useState(50);

    // İmleç yığını: her "Sonraki"de bulunduğumuz sayfanın başlangıç imleci yığına
    // eklenir, "Önceki" onu geri alır. Sayfa numarası olmadığı için geri gitmenin
    // başka yolu yok.
    const [cursorStack, setCursorStack] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [hasMore, setHasMore] = useState(false);

    const [currentUser, setCurrentUser] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [detailId, setDetailId] = useState(null);

    const isSuperAdmin = () => !!currentUser?.roles?.includes("ROLE_SUPER_ADMIN");

    const load = useCallback(async (filters, size, cursor, superAdmin) => {
        setLoading(true);
        setError("");
        try {
            const params = {
                size,
                startDate: filters.startDate ? `${filters.startDate}:00` : "",
                endDate: filters.endDate ? `${filters.endDate}:00` : "",
                category: filters.category,
                outcome: filters.outcome,
                referenceKey: filters.referenceKey.trim(),
                correlationId: filters.correlationId.trim(),
                // Firma filtresi yalnız SUPER_ADMIN için anlamlı
                ...(superAdmin && filters.companyId ? { companyId: filters.companyId } : {}),
                ...(cursor ? { cursorOccurredAt: cursor.occurredAt, cursorId: cursor.id } : {}),
            };

            const data = await fetchAuditLogs(params);
            if (data.statusCode === 200) {
                const d = data.data;
                setRows(d.content || []);
                setHasMore(!!d.hasMore);
                setNextCursor(d.hasMore ? { occurredAt: d.nextOccurredAt, id: d.nextId } : null);
            } else {
                setError(data.message || "Denetim kayıtları getirilemedi");
                setRows([]);
                setHasMore(false);
            }
        } catch {
            setError("Denetim kayıtları getirilemedi");
            setRows([]);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!embedded) document.title = "Denetim İzi | Tahsilat360";

        fetchAuthMe()
            .then((r) => (r.ok ? r.json() : null))
            .then((j) => {
                const me = j?.data || null;
                setCurrentUser(me);
                const superAdmin = !!me?.roles?.includes("ROLE_SUPER_ADMIN");
                // Liste rol belli olduktan sonra çekilir: firma filtresi aksi halde
                // ilk istekte sessizce düşerdi.
                load(EMPTY_FILTERS, pageSize, null, superAdmin);
                if (superAdmin) {
                    return fetchCompaniesList()
                        .then((r) => (r.ok ? r.json() : null))
                        .then((cj) => setCompanies(cj?.data || []));
                }
                return null;
            })
            .catch(() => load(EMPTY_FILTERS, pageSize, null, false));
        // Yalnız ilk açılışta: sonraki yüklemeler filtre/sayfa eylemlerinden tetiklenir.
    }, []);

    const applyFilters = (filters = draft) => {
        setApplied(filters);
        setCursorStack([]);
        load(filters, pageSize, null, isSuperAdmin());
    };

    const clearFilters = () => {
        setDraft(EMPTY_FILTERS);
        applyFilters(EMPTY_FILTERS);
    };

    const goNext = () => {
        if (!nextCursor) return;
        setCursorStack((stack) => [...stack, nextCursor]);
        load(applied, pageSize, nextCursor, isSuperAdmin());
    };

    const goPrevious = () => {
        if (cursorStack.length === 0) return;
        const stack = cursorStack.slice(0, -1);
        setCursorStack(stack);
        load(applied, pageSize, stack[stack.length - 1] || null, isSuperAdmin());
    };

    const changeSize = (size) => {
        setPageSize(size);
        setCursorStack([]);
        load(applied, size, null, isSuperAdmin());
    };

    /** Detaydan gelen "zinciri göster": aynı eylemden doğan tüm olaylara filtrele. */
    const showChain = (correlationId) => {
        const filters = { ...EMPTY_FILTERS, startDate: "", correlationId };
        setDetailId(null);
        setDraft(filters);
        applyFilters(filters);
    };

    const set = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }));

    // Varsayılandan farklı olan alanlar sayılır; son 30 gün penceresi kullanıcının
    // koyduğu bir filtre değil, ekranın açılış hâlidir.
    const activeFilterCount = Object.entries(applied)
        .filter(([key, value]) => value !== EMPTY_FILTERS[key])
        .length;

    const categoryOptions = isSuperAdmin() ? [...CATEGORIES, SECURITY_CATEGORY] : CATEGORIES;
    const showCompany = isSuperAdmin();
    const colCount = showCompany ? 8 : 7;

    return (
        <div className={embedded ? "page-common audit-log" : "page-content page-common audit-log"}>
            <style>{styles}</style>
            <Container fluid className={embedded ? "p-0" : undefined}>

                {/* Başlık ve filtreler tek yüzey: ikisi de sayfanın "ne bakıyorsun /
                    neyi süzüyorsun" bloğu, ayrı kartlarda durunca sayfa parçalı görünüyordu. */}
                <Card className="mb-3">
                    <CardBody>
                        {embedded ? (
                            <p className="text-muted mb-3 pb-3 border-bottom" style={{ fontSize: ".9rem" }}>
                                Para hareketi, yetki ve kimlik ile ilgili işlemlerin kaydı.
                                {!isSuperAdmin() && " Platform personelinin işlemleri \"Sistem Yöneticisi\" olarak görünür."}
                            </p>
                        ) : (
                            <div className="d-flex align-items-center gap-3 mb-3 pb-3 border-bottom">
                                <div className="header-icon">
                                    <i className="bx bx-shield-quarter" />
                                </div>
                                <div>
                                    <h5 className="mb-1 fw-bold text-dark">Denetim İzi</h5>
                                    <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                                        Para hareketi, yetki ve kimlik ile ilgili işlemlerin kaydı.
                                        {!isSuperAdmin() && " Platform personelinin işlemleri \"Sistem Yöneticisi\" olarak görünür."}
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="filter-grid">
                            <div>
                                <label className="form-label small text-muted mb-1">Başlangıç</label>
                                <Input type="datetime-local" value={draft.startDate} onChange={set("startDate")} />
                            </div>
                            <div>
                                <label className="form-label small text-muted mb-1">Bitiş</label>
                                <Input type="datetime-local" value={draft.endDate} onChange={set("endDate")} />
                            </div>
                            <div>
                                <label className="form-label small text-muted mb-1">Kategori</label>
                                <Input type="select" value={draft.category} onChange={set("category")}>
                                    <option value="">Tümü</option>
                                    {categoryOptions.map((c) => (
                                        <option key={c.value} value={c.value}>{c.label}</option>
                                    ))}
                                </Input>
                            </div>
                            <div>
                                <label className="form-label small text-muted mb-1">Sonuç</label>
                                <Input type="select" value={draft.outcome} onChange={set("outcome")}>
                                    <option value="">Tümü</option>
                                    {OUTCOMES.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </Input>
                            </div>
                            {showCompany && (
                                <div>
                                    <label className="form-label small text-muted mb-1">Firma</label>
                                    <Input type="select" value={draft.companyId} onChange={set("companyId")}>
                                        <option value="">Tüm firmalar</option>
                                        {companies.map((c) => (
                                            <option key={c.id} value={c.id}>{c.companyName}</option>
                                        ))}
                                    </Input>
                                </div>
                            )}
                            <div>
                                <label className="form-label small text-muted mb-1">Referans (işlem no)</label>
                                <Input value={draft.referenceKey} onChange={set("referenceKey")} placeholder="merchantOid" />
                            </div>
                            <div>
                                <label className="form-label small text-muted mb-1">İşlem zinciri</label>
                                <Input value={draft.correlationId} onChange={set("correlationId")} placeholder="correlationId" />
                            </div>
                            <div className="d-flex align-items-end gap-2">
                                <Button
                                    className="btn-rounded btn-navy d-flex align-items-center gap-1"
                                    onClick={() => applyFilters()}
                                >
                                    <i className="bx bx-filter-alt" /> Filtrele
                                </Button>
                                {/* Ödeme Geçmişi'ndeki temizle düğmesiyle aynı: varsayılan
                                    dışında filtre yoksa pasif kalır. */}
                                <Button
                                    color="light"
                                    className="btn-rounded"
                                    onClick={clearFilters}
                                    disabled={activeFilterCount === 0}
                                >
                                    <i className="bx bx-x me-1" /> Temizle
                                </Button>
                            </div>
                        </div>
                    </CardBody>
                </Card>

                <Card>
                    <CardBody>
                        {error && <div className="alert alert-danger">{error}</div>}

                        <div className="table-responsive">
                            <Table className="table-nowrap mb-0" hover>
                                <thead>
                                    <tr>
                                        <th>Zaman</th>
                                        <th>Olay</th>
                                        <th>Sonuç</th>
                                        <th>Yapan</th>
                                        {showCompany && <th>Firma</th>}
                                        <th>Etkilenen kayıt</th>
                                        <th>Referans</th>
                                        <th className="text-end">Detay</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading && (
                                        <tr><td colSpan={colCount} className="text-center py-4"><Spinner /></td></tr>
                                    )}

                                    {!loading && rows.length === 0 && (
                                        <tr>
                                            <td colSpan={colCount} className="text-center text-muted py-4">
                                                Seçilen aralıkta kayıt yok.
                                            </td>
                                        </tr>
                                    )}

                                    {!loading && rows.map((row) => (
                                        <tr key={row.id}>
                                            <td className="mono">{formatDateTime(row.occurredAt)}</td>
                                            <td>
                                                <span
                                                    className="cat-dot"
                                                    style={{ background: CATEGORY_COLORS[row.category] || "#adb5bd" }}
                                                    title={row.categoryLabel}
                                                />
                                                {row.actionLabel}
                                            </td>
                                            <td>
                                                <Badge color={OUTCOME_COLORS[row.outcome] || "secondary"}>
                                                    {row.outcomeLabel}
                                                </Badge>
                                            </td>
                                            <td>
                                                {row.actorName || <span className="text-muted">Sistem</span>}
                                                {row.actorEmail && (
                                                    <div className="text-muted small">{row.actorEmail}</div>
                                                )}
                                                {row.impersonated && (
                                                    <Badge color="dark" className="mt-1" title="Bir kullanıcı adına yapıldı">
                                                        Adına işlem
                                                    </Badge>
                                                )}
                                            </td>
                                            {showCompany && (
                                                <td>{row.companyName || <span className="text-muted">Platform</span>}</td>
                                            )}
                                            <td>
                                                {entityTypeLabel(row.entityType) || "—"}
                                                {row.entityId && (
                                                    <div className="text-muted mono">
                                                        <IdValue value={row.entityId} />
                                                    </div>
                                                )}
                                            </td>
                                            <td className="mono">{row.referenceKey || "—"}</td>
                                            <td className="text-end">
                                                <button
                                                    className="btn btn-sm action-btn btn-orange"
                                                    title="Detay"
                                                    onClick={() => setDetailId(row.id)}
                                                >
                                                    <i className="bx bx-show" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>

                        {/* Sayfa numarası yok: toplam kayıt sayısı hesaplanmıyor, yalnız
                            "devamı var mı" bilgisi geliyor. */}
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
                            <div className="d-flex align-items-center gap-2">
                                <span className="text-muted small">Sayfa boyutu</span>
                                <Input
                                    type="select"
                                    style={{ width: 90 }}
                                    value={pageSize}
                                    onChange={(e) => changeSize(Number(e.target.value))}
                                >
                                    {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </Input>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <Button color="light" disabled={cursorStack.length === 0 || loading} onClick={goPrevious}>
                                    Önceki
                                </Button>
                                <Button color="light" disabled={!hasMore || loading} onClick={goNext}>
                                    Sonraki
                                </Button>
                            </div>
                        </div>
                    </CardBody>
                </Card>

                <AuditDetailDrawer
                    logId={detailId}
                    isOpen={!!detailId}
                    onClose={() => setDetailId(null)}
                    onShowChain={showChain}
                />
            </Container>
        </div>
    );
};

export default AuditLogTable;
